import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiDownload, FiEdit2, FiEye, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import salesmanService from "../../../services/salesman.service";
import SalesmanFormModal, {
  EMPTY_SALESMAN_FORM,
  salesmanFromRecord,
} from "./SalesmanFormModal";
import PaginationBar from "../../../components/common/PaginationBar";
import { getApiErrorMessage, saveExcelBlob } from "../../../utils/downloadExcel";
import "../../../styles/users.css";
import "../../../styles/inventoryUpdate.css";
import "../../../styles/bankDetails.css";
import "../../../styles/codeBankNbfc.css";
import "../../../styles/pagination.css";
import "../../../styles/downloadSampleExcelButton.css";

const PAGE_SIZE = 20;

export default function SalesmanManagement() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [success, setSuccess] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [viewOnly, setViewOnly] = useState(false);
  const [formInitial, setFormInitial] = useState(EMPTY_SALESMAN_FORM);
  const [pendingId, setPendingId] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      setListError("");
      const res = await salesmanService.list({
        q: query,
        status: status === "all" ? "" : status,
        page,
        limit: PAGE_SIZE,
      });
      const payload = res?.data?.data || {};
      setRecords(payload.records || []);
      setTotal(payload.total || 0);
      setTotalPages(payload.totalPages || 0);
    } catch (err) {
      setListError(await getApiErrorMessage(err, "Failed to load salesmen."));
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [page, query, status]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const openCreate = () => {
    setFormInitial(EMPTY_SALESMAN_FORM);
    setEditingId("");
    setViewOnly(false);
    setSuccess("");
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setFormInitial(salesmanFromRecord(row));
    setEditingId(row.id);
    setViewOnly(false);
    setSuccess("");
    setFormOpen(true);
  };

  const openView = (row) => {
    setFormInitial(salesmanFromRecord(row));
    setEditingId(row.id);
    setViewOnly(true);
    setFormOpen(true);
  };

  const handleSaved = () => {
    setFormOpen(false);
    setSuccess(editingId ? "Salesman updated." : "Salesman saved.");
    loadRecords();
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      setListError("");
      const response = await salesmanService.downloadExcel({
        q: query,
        status: status === "all" ? "" : status,
      });
      await saveExcelBlob(response, "FastSearch-salesmen.xlsx");
    } catch (err) {
      setListError(await getApiErrorMessage(err, "Failed to download Excel."));
    } finally {
      setDownloading(false);
    }
  };

  const downloadSample = async () => {
    setUploadError("");
    try {
      const response = await salesmanService.downloadSample();
      await saveExcelBlob(response, "FastSearch-salesman-sample.xlsx");
    } catch (err) {
      setUploadError(await getApiErrorMessage(err, "Could not download the sample Excel."));
    }
  };

  const takeExcelFile = (file) => {
    if (!file) return;
    setPreview(null);
    setUploadError("");
    setUploadFile(file);
  };

  const runPreview = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError("");
    try {
      const res = await salesmanService.previewImport(uploadFile);
      setPreview(res.data);
    } catch (err) {
      setUploadError(await getApiErrorMessage(err, "Could not check this Excel file."));
    } finally {
      setUploading(false);
    }
  };

  const confirmImport = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError("");
    try {
      const res = await salesmanService.commitImport(uploadFile);
      setSuccess(res?.data?.message || "Salesmen imported.");
      setUploadOpen(false);
      setUploadFile(null);
      setPreview(null);
      await loadRecords();
    } catch (err) {
      setPreview(err?.response?.data || null);
      setUploadError(await getApiErrorMessage(err, "Import failed."));
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setPendingId(pendingDelete.id);
      setListError("");
      await salesmanService.remove(pendingDelete.id);
      setSuccess("Salesman deleted.");
      setPendingDelete(null);
      await loadRecords();
    } catch (err) {
      setListError(await getApiErrorMessage(err, "Failed to delete salesman."));
    } finally {
      setPendingId("");
    }
  };

  const startIndex = useMemo(() => (page - 1) * PAGE_SIZE, [page]);

  return (
    <div className="page">
      <div className="cbn-header">
        <h2>Salesman Management</h2>
        <div className="cbn-header-actions">
          <button
            type="button"
            className="btn-table-excel"
            onClick={handleDownloadExcel}
            disabled={downloading || loading}
          >
            <FiDownload aria-hidden />
            <span>{downloading ? "Preparing file…" : "Download Excel"}</span>
          </button>
          <button type="button" className="secondary-page-btn" onClick={() => setUploadOpen(true)}>
            Upload Excel
          </button>
          <button type="button" className="primary-page-btn" onClick={openCreate}>
            <FiPlus /> Add
          </button>
        </div>
      </div>

      {success ? <p className="inv-success">{success}</p> : null}
      {listError ? <p className="error-text">{listError}</p> : null}

      <div className="bd-form-card cbn-filters">
        <div className="bd-form-grid">
          <label className="bd-field">
            <span className="bd-label">Search</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Salesman name or mobile number"
            />
          </label>
          <label className="bd-field">
            <span className="bd-label">Status</span>
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading salesmen…</p>
      ) : records.length === 0 ? (
        <p className="muted">No salesman records match this search.</p>
      ) : (
        <div className="company-table-wrap bd-excel-sheet">
          <table className="users-table excel-grid-table bd-excel-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Salesman Name</th>
                <th>Mobile No.</th>
                <th>Email</th>
                <th>Company / Distributor Name</th>
                <th className="cbn-col-address">Address</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((row, index) => (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td>{row.salesmanName}</td>
                  <td>{row.mobile}</td>
                  <td>{row.email || "—"}</td>
                  <td>{row.distributorName || "—"}</td>
                  <td className="cbn-col-address">{row.address || "—"}</td>
                  <td className="bd-col-status">
                    <span
                      className={`bd-status-badge ${
                        row.isActive ? "bd-status-badge--uploaded" : "bd-status-badge--upload"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td>
                    <div className="cbn-actions">
                      <button
                        type="button"
                        className="cbn-icon-btn"
                        title="View"
                        aria-label={`View ${row.salesmanName}`}
                        disabled={pendingId === row.id}
                        onClick={() => openView(row)}
                      >
                        <FiEye />
                      </button>
                      <button
                        type="button"
                        className="cbn-icon-btn"
                        title="Edit"
                        aria-label={`Edit ${row.salesmanName}`}
                        disabled={pendingId === row.id}
                        onClick={() => openEdit(row)}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        className="cbn-icon-btn cbn-icon-btn--danger"
                        title="Delete"
                        aria-label={`Delete ${row.salesmanName}`}
                        disabled={pendingId === row.id}
                        onClick={() => setPendingDelete(row)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PaginationBar
        page={page}
        totalPages={totalPages}
        total={total}
        loading={loading}
        onPageChange={setPage}
      />

      {formOpen ? (
        <SalesmanFormModal
          editingId={editingId}
          initial={formInitial}
          viewOnly={viewOnly}
          onClose={() => setFormOpen(false)}
          onSaved={handleSaved}
        />
      ) : null}

      {uploadOpen ? (
        <div className="modal-overlay" onClick={() => !uploading && setUploadOpen(false)} role="presentation">
          <div
            className="modal-box bd-upload-modal mm-sheet"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-labelledby="salesman-upload-title"
          >
            <div className="mm-sheet__head">
              <div>
                <h3 id="salesman-upload-title">Bulk Salesman Upload</h3>
                <p className="modal-subtitle">Upload multiple salesman records using an Excel file.</p>
              </div>
              <button
                type="button"
                className="mm-sheet__close"
                onClick={() => !uploading && setUploadOpen(false)}
                aria-label="Close"
              >
                <FiX aria-hidden />
              </button>
            </div>
            <div className="mm-sheet__body">
              <div className="sample-excel-banner">
                <div className="sample-excel-banner__content">
                  <p className="sample-excel-banner__title">Excel column layout</p>
                  <p className="sample-excel-banner__desc">
                    Download the sample first. Keep the same headers, then fill salesman details.
                  </p>
                </div>
                <button
                  type="button"
                  className="sample-excel-btn sample-excel-btn--solid"
                  onClick={downloadSample}
                  disabled={uploading}
                >
                  <span className="sample-excel-btn__label">Download Sample Excel</span>
                  <FiDownload className="sample-excel-btn__arrow" aria-hidden />
                </button>
              </div>
              <div
                className="mm-drop"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  takeExcelFile(event.dataTransfer.files?.[0]);
                }}
              >
                <p className="bd-muted">Drag and drop an .xlsx or .xls file here, or choose a file.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={(event) => takeExcelFile(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className="bd-btn bd-btn-secondary bd-btn-compact"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  Choose Excel file
                </button>
                {uploadFile ? <p className="mm-file-name">{uploadFile.name}</p> : null}
              </div>
              {uploadError ? <p className="bd-feedback bd-error">{uploadError}</p> : null}
              {preview?.summary ? (
                <>
                  <div className="bd-form-grid">
                    <p className="bd-muted">Total Excel rows: {preview.summary.totalRows}</p>
                    <p className="bd-muted">Valid rows: {preview.summary.validRows}</p>
                    <p className="bd-muted">Invalid rows: {preview.summary.invalidRows}</p>
                  </div>
                  {preview.errors?.length ? (
                    <div className="company-table-wrap bd-excel-sheet">
                      <table className="users-table excel-grid-table bd-excel-table">
                        <thead>
                          <tr>
                            <th>Excel Row</th>
                            <th>Salesman</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.errors.map((error) => (
                            <tr key={`${error.row}-${error.message}`}>
                              <td>{error.row}</td>
                              <td>{error.salesman}</td>
                              <td>{error.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </>
              ) : null}
              <div className="bd-form-actions">
                <button
                  type="button"
                  className="bd-btn bd-btn-secondary"
                  disabled={uploading}
                  onClick={() => setUploadOpen(false)}
                >
                  Cancel
                </button>
                {!preview ? (
                  <button
                    type="button"
                    className="bd-btn bd-btn-primary"
                    disabled={uploading || !uploadFile}
                    onClick={runPreview}
                  >
                    {uploading ? "Checking…" : "Upload Excel"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bd-btn bd-btn-primary"
                    disabled={uploading || !preview.summary?.validRows}
                    onClick={confirmImport}
                  >
                    {uploading ? "Importing…" : "Confirm Import"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDelete ? (
        <div className="modal-overlay" onClick={() => setPendingDelete(null)} role="presentation">
          <div className="modal-box" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>Delete salesman</h3>
            <p className="modal-subtitle">
              Delete {pendingDelete.salesmanName}? Existing medicine purchase records keep the saved salesman name
              and mobile.
            </p>
            <div className="modal-actions">
              <button type="button" className="secondary-page-btn" onClick={() => setPendingDelete(null)}>
                Cancel
              </button>
              <button type="button" className="primary-page-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
